import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import {
  fetchSummary,
  fetchActiveUsers,
  fetchCustomerSummary,
  fetchUserSummary,
  fetchEnrichedEvents,
} from './analyticsApi';
import type {
  ActiveUsersRow,
  CustomerSummaryRow,
  EnrichedEventOut,
  SummaryRow,
  UserSummaryRow,
} from './analyticsTypes';
import { readAxiosDetail } from '../api_calls';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { useAnalyticsFilters, previousPeriod } from './hooks/useAnalyticsFilters';
import { useInspector } from './hooks/useInspector';
import KpiTile from './components/KpiTile';
import RankedList, { RankedListItem } from './components/RankedList';
import CategoryDonutChart from './components/CategoryDonutChart';
import FunnelWidget from './components/FunnelWidget';
import CustomFunnels from './components/CustomFunnels';
import EmptyState from './components/EmptyState';
import ActiveUsersChart from './ActiveUsersChart';
import EventDistributionChart from './EventDistributionChart';
import {
  computeFunnel,
  WIZARD_FUNNEL,
  POSTCODE_FUNNEL,
  FLEET_OPTIMISER_FUNNEL,
  SIMULATION_FUNNEL,
  FunnelStepResult,
} from './utils/computeFunnel';
import { countDistinctSessions } from './utils/countDistinctSessions';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
const SIMULATION_COMPLETE_EVENT = 'simulation_run_complete';

interface OverviewState {
  summary: SummaryRow[];
  active: ActiveUsersRow[];
  distinctSessions: number;
  customers: CustomerSummaryRow[];
  users: UserSummaryRow[];
  scrape: EnrichedEventOut[];
  prev: {
    summary: SummaryRow[];
    active: ActiveUsersRow[];
    distinctSessions: number;
    customers: CustomerSummaryRow[];
    users: UserSummaryRow[];
  } | null;
}

const SCRAPE_SAMPLE_SIZE = 1000;
const SCRAPE_PAGE_SIZE = 200; // backend caps enriched events at 200

async function fetchEnrichedEventsSample(
  baseParams: { since?: string; until?: string },
  signal: AbortSignal,
) {
  const items: EnrichedEventOut[] = [];
  let page = 1;
  let total = 0;
  while (items.length < SCRAPE_SAMPLE_SIZE) {
    if (signal.aborted) break;
    const res = await fetchEnrichedEvents({ ...baseParams, page, page_size: SCRAPE_PAGE_SIZE });
    total = res.total;
    items.push(...res.items);
    if (res.items.length < SCRAPE_PAGE_SIZE || items.length >= res.total) break;
    page += 1;
  }
  return { items: items.slice(0, SCRAPE_SAMPLE_SIZE), total, page: 1, page_size: items.length };
}

export default function OverviewSection() {
  const { filters, setFilters } = useAnalyticsFilters();
  const inspector = useInspector();
  const [state, setState] = useState<OverviewState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPercent, setShowPercent] = useState(false);

  const since = filters.since;
  const until = filters.until;
  const compare = filters.compare;
  const prevRange = compare && since && until ? previousPeriod(since, until) : null;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const baseParams = { since, until };
    const dauDays = computeDauDays(since, until);

    const fetchCurrent = Promise.all([
      fetchSummary(baseParams),
      fetchActiveUsers(dauDays),
      fetchCustomerSummary({ page_size: 50, ...baseParams }),
      fetchUserSummary({ page_size: 50, ...baseParams }),
      fetchEnrichedEventsSample(baseParams, controller.signal),
    ]);

    const fetchPrev = prevRange
      ? Promise.all([
          fetchSummary(prevRange),
          fetchActiveUsers(dauDays),
          fetchCustomerSummary({ page_size: 50, ...prevRange }),
          fetchUserSummary({ page_size: 50, ...prevRange }),
          fetchEnrichedEventsSample(prevRange, controller.signal),
        ])
      : Promise.resolve(null);

    Promise.all([fetchCurrent, fetchPrev])
      .then(([current, prev]) => {
        if (controller.signal.aborted) return;
        const [summary, active, customers, users, scrape] = current;
        setState({
          summary: summary.rows,
          active: active.rows,
          distinctSessions: countDistinctSessions(scrape.items),
          customers: customers.items,
          users: users.items,
          scrape: scrape.items,
          prev: prev
            ? {
                summary: prev[0].rows,
                active: prev[1].rows,
                distinctSessions: countDistinctSessions(prev[4].items),
                customers: prev[2].items,
                users: prev[3].items,
              }
            : null,
        });
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(readAxiosDetail(err));
        setLoading(false);
      });

    return () => controller.abort();
  }, [since, until, compare, prevRange?.since, prevRange?.until]);

  // ----- KPIs -----
  const kpis = useMemo(() => {
    const totalEvents = (state?.summary ?? []).reduce((s, r) => s + r.count, 0);
    const distinctUsers = state?.users.length ?? 0;
    const distinctCustomers = state?.customers.length ?? 0;
    const avgPerUser = distinctUsers === 0 ? 0 : totalEvents / distinctUsers;
    const simulationCompletions = (state?.summary ?? [])
      .filter((r) => r.event_name === SIMULATION_COMPLETE_EVENT)
      .reduce((s, r) => s + r.count, 0);

    const prevTotalEvents = state?.prev ? state.prev.summary.reduce((s, r) => s + r.count, 0) : null;
    const prevUsers = state?.prev ? state.prev.users.length : null;
    const prevCustomers = state?.prev ? state.prev.customers.length : null;
    const prevAvg = state?.prev && (state.prev.users.length > 0)
      ? state.prev.summary.reduce((s, r) => s + r.count, 0) / state.prev.users.length
      : null;
    const prevSimulationCompletions = state?.prev
      ? state.prev.summary
          .filter((r) => r.event_name === SIMULATION_COMPLETE_EVENT)
          .reduce((s, r) => s + r.count, 0)
      : null;
    const distinctSessions = state?.distinctSessions ?? 0;
    const prevSessions = state?.prev?.distinctSessions ?? null;

    const dauTrend = (state?.active ?? []).map((r) => r.distinct_users);

    return {
      totalEvents,
      distinctUsers,
      distinctSessions,
      distinctCustomers,
      avgPerUser,
      simulationCompletions,
      prevTotalEvents,
      prevUsers,
      prevSessions,
      prevCustomers,
      prevAvg,
      prevSimulationCompletions,
      dauTrend,
    };
  }, [state]);

  // ----- Top customers / users with comparison deltas -----
  const customerItems = useMemo<RankedListItem[]>(() => {
    if (!state) return [];
    const prevMap = new Map<string, number>();
    state.prev?.customers.forEach((c) => {
      if (c.customer_id) prevMap.set(c.customer_id, c.total_events);
    });
    return state.customers.map((c) => ({
      id: c.customer_id ?? c.customer_name ?? '',
      primary: c.customer_name ?? c.customer_id ?? '—',
      secondary: c.customer_id ?? undefined,
      value: c.total_events,
      previousValue: c.customer_id ? prevMap.get(c.customer_id) ?? null : null,
    }));
  }, [state]);

  const userItems = useMemo<RankedListItem[]>(() => {
    if (!state) return [];
    const prevMap = new Map<string, number>();
    state.prev?.users.forEach((u) => {
      if (u.actor_id) prevMap.set(u.actor_id, u.total_events);
    });
    return state.users.map((u) => ({
      id: u.actor_id ?? u.username ?? '',
      primary: u.username ?? u.actor_id ?? '—',
      secondary: u.actor_id ?? undefined,
      value: u.total_events,
      previousValue: u.actor_id ? prevMap.get(u.actor_id) ?? null : null,
    }));
  }, [state]);

  // ----- Top screens (from enriched scrape) -----
  const screenItems = useMemo<RankedListItem[]>(() => {
    if (!state) return [];
    const counts = new Map<string, number>();
    for (const ev of state.scrape) {
      const key = ev.screen ?? '(no screen)';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([screen, count]) => ({
        id: screen,
        primary: screen,
        value: count,
      }));
  }, [state]);

  // ----- Funnels -----
  const wizardSteps = useMemo<FunnelStepResult[]>(() => state ? computeFunnel(state.scrape, WIZARD_FUNNEL) : [], [state]);
  const postcodeSteps = useMemo<FunnelStepResult[]>(() => state ? computeFunnel(state.scrape, POSTCODE_FUNNEL) : [], [state]);
  const fleetSteps = useMemo<FunnelStepResult[]>(() => state ? computeFunnel(state.scrape, FLEET_OPTIMISER_FUNNEL) : [], [state]);
  const simulationSteps = useMemo<FunnelStepResult[]>(() => state ? computeFunnel(state.scrape, SIMULATION_FUNNEL) : [], [state]);

  // ----- Click-throughs -----
  const drillEvent = (eventName: string) => {
    trackAnalyticsEvent('admin_analytics_chart_drilled', {
      source_chart: 'top_events',
      dimension: 'event_name',
      timestamp: new Date().toISOString(),
    });
    setFilters({ eventName, view: 'events' });
  };
  const drillCategory = (category: string) => {
    trackAnalyticsEvent('admin_analytics_chart_drilled', {
      source_chart: 'category_donut',
      dimension: 'category',
      timestamp: new Date().toISOString(),
    });
    setFilters({ category, view: 'events' });
  };
  const drillDay = (day: string) => {
    trackAnalyticsEvent('admin_analytics_chart_drilled', {
      source_chart: 'dau_trend',
      dimension: 'day',
      timestamp: new Date().toISOString(),
    });
    setFilters({ since: day, until: day, preset: 'custom', view: 'events' });
  };

  // ----- Render -----
  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KPI grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
        }}
      >
        <KpiTile
          icon={<Icon name="calendar-day" size={20} />}
          label="Total events"
          value={kpis.totalEvents}
          previousValue={kpis.prevTotalEvents ?? undefined}
          loading={loading}
        />
        <KpiTile
          icon={<Icon name="clock-rotate-left" size={20} />}
          label="Sessions"
          subtitle="Website visits"
          value={kpis.distinctSessions}
          previousValue={kpis.prevSessions ?? undefined}
          loading={loading}
        />
        <KpiTile
          icon={<Icon name="users" size={20} />}
          label="Distinct users"
          value={kpis.distinctUsers}
          previousValue={kpis.prevUsers ?? undefined}
          trend={kpis.dauTrend}
          loading={loading}
        />
        <KpiTile
          icon={<Icon d={ICON_FALLBACKS.Business} size={20} />}
          label="Distinct customers"
          value={kpis.distinctCustomers}
          previousValue={kpis.prevCustomers ?? undefined}
          loading={loading}
        />
        <KpiTile
          icon={<Icon d={ICON_FALLBACKS.Insights} size={20} />}
          label="Avg events / user"
          value={kpis.avgPerUser.toFixed(1)}
          previousValue={kpis.prevAvg ?? undefined}
          loading={loading}
        />
        <KpiTile
          icon={<Icon d={ICON_FALLBACKS.PlayCircleOutline} size={20} />}
          label="Successful sim runs"
          value={kpis.simulationCompletions}
          previousValue={kpis.prevSimulationCompletions ?? undefined}
          loading={loading}
        />
      </Box>

      {/* DAU trend */}
      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <Skeleton variant="rectangular" height={280} />
          ) : (state?.active.length ?? 0) === 0 ? (
            <EmptyState title="No activity in this window" />
          ) : (
            <ActiveUsersChart
              rows={state!.active}
              previousRows={state?.prev?.active}
              onPointClick={drillDay}
            />
          )}
        </CardContent>
      </Card>

      {/* Donut + Top events */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
        <Card variant="outlined">
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon d={ICON_FALLBACKS.PieChart} size={20} />
                <Typography variant="subtitle1" fontWeight={700}>Events by category</Typography>
              </Stack>
            }
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 1 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={240} />
            ) : (state?.summary.length ?? 0) === 0 ? (
              <EmptyState compact title="No events" />
            ) : (
              <CategoryDonutChart rows={state!.summary} onSliceClick={drillCategory} />
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon d={ICON_FALLBACKS.Leaderboard} size={20} />
                <Typography variant="subtitle1" fontWeight={700}>Top events</Typography>
              </Stack>
            }
            action={
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showPercent}
                    onChange={(_, c) => setShowPercent(c)}
                  />
                }
                label={<Typography variant="caption">Show as %</Typography>}
              />
            }
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 1 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (state?.summary.length ?? 0) === 0 ? (
              <EmptyState compact title="No events" />
            ) : (
              <EventDistributionChart
                rows={state!.summary}
                asPercent={showPercent}
                onBarClick={drillEvent}
              />
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Top screens + Top customers + Top users */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
        <RankedList
          title="Top screens"
          icon={<Icon d={ICON_FALLBACKS.Monitor} size={20} />}
          items={screenItems}
          loading={loading}
          emptyTitle="No screen data"
          emptyBody={`Computed from a sample of up to ${SCRAPE_SAMPLE_SIZE.toLocaleString()} events.`}
          valueLabel="Hits"
          onItemClick={(item) => {
            if (item.id && item.id !== '(no screen)') setFilters({ screen: item.id, view: 'events' });
          }}
        />
        <RankedList
          title="Top customers"
          icon={<Icon d={ICON_FALLBACKS.Business} size={20} />}
          items={customerItems}
          loading={loading}
          onItemClick={(item) => {
            if (item.id) inspector.open({ kind: 'customer', customerId: item.id, customerName: item.primary });
          }}
        />
        <RankedList
          title="Top users"
          icon={<Icon name="users" size={20} />}
          items={userItems}
          loading={loading}
          onItemClick={(item) => {
            if (item.id) inspector.open({ kind: 'user', actorId: item.id, username: item.primary });
          }}
        />
      </Box>

      {/* Funnels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
        <FunnelWidget
          title="Simulation run"
          icon={<Icon d={ICON_FALLBACKS.PlayCircleOutline} size={20} />}
          steps={simulationSteps}
          loading={loading}
          helperText="Started → completed → results interacted with"
        />
        <FunnelWidget
          title="Onboarding wizard"
          icon={<Icon d={ICON_FALLBACKS.School} size={20} />}
          steps={wizardSteps}
          loading={loading}
          helperText="Sessions that progressed through the quick-start wizard"
        />
        <FunnelWidget
          title="Postcode Stop Assignment"
          icon={<Icon name="location-dot" size={20} />}
          steps={postcodeSteps}
          loading={loading}
          helperText="File added → run started → completed"
        />
        <FunnelWidget
          title="Fleet Optimiser"
          icon={<Icon name="bus" size={20} />}
          steps={fleetSteps}
          loading={loading}
          helperText="Configured → analyzed → result displayed"
        />
      </Box>

      <CustomFunnels scrape={state?.scrape ?? []} loading={loading} />

      <Typography variant="caption" color="text.disabled" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Icon size={14} d={ICON_FALLBACKS.Label} />
        Funnels and screen rankings are computed client-side from a sample of up to {SCRAPE_SAMPLE_SIZE.toLocaleString()} events. <Icon size={14} name="route" style={{ marginLeft: 4 }} /> Click any chart bar, slice, or row to drill into the Events Explorer.
      </Typography>
    </Stack>
  );
}

function computeDauDays(since?: string, until?: string): number {
  if (!since || !until) return 30;
  const s = new Date(since + 'T00:00:00Z').getTime();
  const u = new Date(until + 'T00:00:00Z').getTime();
  if (Number.isNaN(s) || Number.isNaN(u) || u < s) return 30;
  const days = Math.round((u - s) / (24 * 60 * 60 * 1000)) + 1;
  return Math.max(1, Math.min(days, 365));
}
